import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './ChangePromptModal.css';

const ChangePromptModal = ({
  reflection,
  prompts,
  aiEnabled,
  onClose,
  onAccept,
  onPromptCreated
}) => {
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [selectedSuggestionId, setSelectedSuggestionId] = useState(null);
  const [selectNewSuggestion, setSelectNewSuggestion] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [newSuggestion, setNewSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingNewPrompt, setGeneratingNewPrompt] = useState(false);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  // Searchable dropdown state
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = React.useRef(null);

  // Fetch AI suggestions (embeddings-based) when modal opens
  useEffect(() => {
    if (aiEnabled && reflection) {
      fetchAiSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiEnabled, reflection?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter prompts based on search text (case-insensitive contains search)
  const filteredPrompts = prompts.filter(prompt => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const promptText = prompt.prompt_text.toLowerCase();
    const categoryName = prompt.category_name.toLowerCase();
    return promptText.includes(searchLower) || categoryName.includes(searchLower);
  });

  const fetchAiSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(
        `/reflections/${reflection.id}/suggest-prompts`,
        {}
      );
      setAiSuggestions(response.data.suggestions || []);
      // Note: new_suggestion is no longer returned automatically - user must request it
    } catch (err) {
      console.error('Failed to fetch AI suggestions:', err);
      setError(err.response?.data?.error || 'Failed to get AI suggestions');
    } finally {
      setLoading(false);
    }
  };

  // Generate a new prompt suggestion on demand (user-initiated)
  const generateNewPrompt = async () => {
    setGeneratingNewPrompt(true);
    setError(null);
    try {
      const response = await api.post(
        `/reflections/${reflection.id}/generate-new-prompt`,
        {}
      );
      if (response.data.new_suggestion) {
        setNewSuggestion(response.data.new_suggestion);
        // Automatically select the new suggestion
        setSelectNewSuggestion(true);
        setSelectedSuggestionId(null);
        setSelectedPromptId('');
        setSearchText('');
      } else {
        setError('Could not generate a new prompt for this content');
      }
    } catch (err) {
      console.error('Failed to generate new prompt:', err);
      setError(err.response?.data?.error || 'Failed to generate new prompt');
    } finally {
      setGeneratingNewPrompt(false);
    }
  };

  // Handle search input change - only filters, doesn't select
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setShowDropdown(true);
    // Don't clear selection until user actually picks a new prompt
  };

  // Handle selecting a prompt from the filtered list - clears radio buttons
  const handlePromptSelect = (promptId) => {
    const prompt = prompts.find(p => p.id === promptId);
    if (prompt) {
      setSelectedPromptId(promptId);
      setSearchText(formatPrompt(prompt.category_name, prompt.prompt_text));
      setSelectedSuggestionId(null);
      setSelectNewSuggestion(false);
      setShowDropdown(false);
    }
  };

  // Handle suggestion radio selection - clear dropdown selection
  const handleSuggestionSelect = (suggestionId) => {
    setSelectedSuggestionId(suggestionId);
    setSelectNewSuggestion(false);
    setSelectedPromptId('');
    setSearchText('');
    setShowDropdown(false);
  };

  // Handle new suggestion selection - clear dropdown and other radios
  const handleNewSuggestionSelect = () => {
    setSelectNewSuggestion(true);
    setSelectedSuggestionId(null);
    setSelectedPromptId('');
    setSearchText('');
    setShowDropdown(false);
  };

  // Check if any selection is made
  const hasSelection = selectedPromptId || selectedSuggestionId || selectNewSuggestion;

  // Handle accept
  const handleAccept = async () => {
    if (!hasSelection) return;

    setAccepting(true);
    try {
      let finalPromptId;

      if (selectNewSuggestion && newSuggestion) {
        // Create new prompt first
        // Find category ID from name
        const category = prompts.find(p => p.category_name === newSuggestion.category);
        let categoryId = category?.category_id;

        // If category not found, use General (usually ID 1, but let's find it)
        if (!categoryId) {
          const generalPrompt = prompts.find(p =>
            p.category_name.toLowerCase() === 'general'
          );
          categoryId = generalPrompt?.category_id || 1;
        }

        const createResponse = await api.post(
          '/prompts',
          { promptText: newSuggestion.prompt, categoryId }
        );

        finalPromptId = createResponse.data.id;

        // Notify parent that a new prompt was created
        if (onPromptCreated) {
          onPromptCreated(createResponse.data);
        }
      } else if (selectedSuggestionId) {
        finalPromptId = selectedSuggestionId;
      } else {
        finalPromptId = parseInt(selectedPromptId);
      }

      // Now change the reflection's prompt
      await api.put(
        `/reflections/${reflection.id}/prompt`,
        { promptId: finalPromptId }
      );

      onAccept(finalPromptId);
    } catch (err) {
      console.error('Failed to change prompt:', err);
      setError(err.response?.data?.error || 'Failed to change prompt');
      setAccepting(false);
    }
  };

  // Format prompt for display: "(Category) - Prompt text"
  const formatPrompt = (categoryName, promptText) => {
    return `(${categoryName}) - ${promptText}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="change-prompt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Change Prompt</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Searchable dropdown for all prompts */}
          <div className="form-group">
            <label htmlFor="prompt-search">Prompt</label>
            <div className="searchable-dropdown" ref={dropdownRef}>
              <input
                id="prompt-search"
                type="text"
                value={searchText}
                onChange={handleSearchChange}
                onFocus={() => setShowDropdown(true)}
                placeholder="Type to search prompts..."
                className="prompt-search-input"
                autoComplete="off"
              />
              {showDropdown && (
                <div className="prompt-dropdown-list">
                  {filteredPrompts.length === 0 ? (
                    <div className="dropdown-no-results">No matching prompts</div>
                  ) : (
                    filteredPrompts.map(prompt => (
                      <div
                        key={prompt.id}
                        className={`dropdown-item ${selectedPromptId === prompt.id ? 'selected' : ''}`}
                        onClick={() => handlePromptSelect(prompt.id)}
                      >
                        {formatPrompt(prompt.category_name, prompt.prompt_text)}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* AI Suggestions Section */}
          {aiEnabled && (
            <div className="ai-section">
              <h3 className="ai-section-header">AI Suggestions</h3>

              {loading && (
                <div className="ai-loading">
                  <span className="spinner"></span>
                  Analyzing reflection content...
                </div>
              )}

              {error && (
                <div className="ai-error">{error}</div>
              )}

              {!loading && !error && aiSuggestions.length === 0 && !newSuggestion && (
                <div className="ai-no-suggestions">
                  No matching prompts found for this reflection.
                </div>
              )}

              {!loading && aiSuggestions.length > 0 && (
                <div className="suggestions-list">
                  {aiSuggestions.map((suggestion, index) => (
                    <label key={suggestion.id} className="suggestion-item">
                      <input
                        type="radio"
                        name="suggestion"
                        checked={selectedSuggestionId === suggestion.id}
                        onChange={() => handleSuggestionSelect(suggestion.id)}
                      />
                      <span className="suggestion-text">
                        {formatPrompt(suggestion.category, suggestion.prompt)}
                      </span>
                      {index === 0 && <span className="best-match-badge">Best Match</span>}
                    </label>
                  ))}
                </div>
              )}

              {/* Generate New Prompt Section */}
              {!loading && (
                <div className="new-prompt-section">
                  <h4 className="new-prompt-header">Create New Prompt</h4>
                  {!newSuggestion ? (
                    <div className="generate-prompt-action">
                      <button
                        className="btn-generate-prompt"
                        onClick={generateNewPrompt}
                        disabled={generatingNewPrompt}
                      >
                        {generatingNewPrompt ? (
                          <>
                            <span className="spinner"></span>
                            Generating...
                          </>
                        ) : (
                          'Generate New Prompt with AI'
                        )}
                      </button>
                      <p className="generate-prompt-hint">
                        AI will analyze your reflection and suggest a new prompt
                      </p>
                    </div>
                  ) : (
                    <label className="suggestion-item new-suggestion">
                      <input
                        type="radio"
                        name="suggestion"
                        checked={selectNewSuggestion}
                        onChange={handleNewSuggestionSelect}
                      />
                      <span className="suggestion-text">
                        {formatPrompt(newSuggestion.category, newSuggestion.prompt)}
                      </span>
                      <span className="new-prompt-badge">New</span>
                    </label>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button
            className="btn-cancel"
            onClick={onClose}
            disabled={accepting}
          >
            Cancel
          </button>
          <button
            className="btn-accept"
            onClick={handleAccept}
            disabled={!hasSelection || accepting}
          >
            {accepting ? 'Updating...' : 'Accept'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePromptModal;
